import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { TimeZone } from '../../time/timezone.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { GenericOrder } from './orders/generic-order.js';

export class GenericOrderEntryCanceledEvent extends SyncEvent {
  static readonly TYPE_ID: string = 'cc3afe1fa45c4f67ac7c5512157b3d86';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly order: GenericOrder,
    public readonly orderEntryId: string,
    public readonly isRefund: boolean,
  ) {
    super();
  }

  get $typeId(): string {
    return GenericOrderEntryCanceledEvent.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    order: GenericOrder,
    orderEntryId: string,
    isRefund: boolean,
  }): GenericOrderEntryCanceledEvent {
    return new GenericOrderEntryCanceledEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.order,
        args.orderEntryId,
        args.isRefund,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrderEntryCanceledEvent {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getStringOrNull(map, 'creatorId'),
      deviceId: MapUtils.getStringOrNull(map, 'deviceId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      source: toEnumFromString(MapUtils.getString(map, 'source'), SyncEventSource),
      order: context.fromData<GenericOrder>(MapUtils.getRecord(map, 'order')),
      orderEntryId: MapUtils.getString(map, 'orderEntryId'),
      isRefund: MapUtils.getBoolean(map, 'isRefund'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'order': context.toData(this.order),
      'orderEntryId': this.orderEntryId,
      'isRefund': this.isRefund,
    };
  }

  clone(): this {
    return GenericOrderEntryCanceledEvent.create({
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      source: this.source,
      order: cloneData(this.order),
      orderEntryId: this.orderEntryId,
      isRefund: this.isRefund,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
           other.creatorId === this.creatorId &&
           other.deviceId === this.deviceId &&
           TimeZone.isSameInstant(other.creationDate, this.creationDate) &&
           other.source === this.source &&
           other.orderEntryId === this.orderEntryId &&
           other.isRefund === this.isRefund &&
           isSameData(other.order, this.order);
  }

  static verifyType(model: DataModel): boolean {
    return model instanceof GenericOrderEntryCanceledEvent;
  }

  static assertType(model: DataModel): model is GenericOrderEntryCanceledEvent {
    return model instanceof GenericOrderEntryCanceledEvent;
  }
}

export class GenericOrderEntryCanceledEventBuilder extends DataModelBuilder<GenericOrderEntryCanceledEvent> {
  build(context: SerializationContext, map: SerializedData): GenericOrderEntryCanceledEvent {
    return GenericOrderEntryCanceledEvent.fromMap(context, map);
  }
}
